import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import useStoreSettings from "@/hooks/useStoreSettings";
import AdminLayout from "@/components/feature/AdminLayout";
import type { Profile, Order, Product, RechargeRequest, Withdrawal, KycSubmission } from "@/lib/supabase";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();
  const { settings } = useStoreSettings();

  const [users, setUsers] = useState<Profile[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [rechargeRequests, setRechargeRequests] = useState<RechargeRequest[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [kycSubmissions, setKycSubmissions] = useState<KycSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Password change state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState("");

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [lineUrl, setLineUrl] = useState(settings.site_line_url || "https://line.me");
  const [settingsMsg, setSettingsMsg] = useState("");

  // Month selector for trend table
  const [selectedMonth, setSelectedMonth] = useState<string>(
    new Date().toISOString().slice(0, 7)
  );

  const loadData = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const [u, o, p, r, w, k] = await Promise.all([
        supabase.from("profiles").select("*").order("created_at", { ascending: false }),
        supabase.from("orders").select("*").order("created_at", { ascending: false }),
        supabase.from("products").select("*").order("created_at", { ascending: false }),
        supabase.from("recharge_requests").select("*").order("created_at", { ascending: false }),
        supabase.from("withdrawals").select("*").order("created_at", { ascending: false }),
        supabase.from("kyc_submissions").select("*").order("submitted_at", { ascending: false }),
      ]);

      setUsers((u.data as Profile[]) || []);
      setOrders((o.data as Order[]) || []);
      setProducts((p.data as Product[]) || []);
      setRechargeRequests((r.data as RechargeRequest[]) || []);
      setWithdrawals((w.data as Withdrawal[]) || []);
      setKycSubmissions((k.data as KycSubmission[]) || []);

      const errors = [];
      if (u.error) errors.push("會員");
      if (o.error) errors.push("訂單");
      if (p.error) errors.push("商品");
      if (r.error) errors.push("儲值");
      if (w.error) errors.push("提現");
      if (k.error) errors.push("實名");
      if (errors.length > 0) {
        setLoadError(`無法讀取：${errors.join("、")}資料。請確認管理員權限已正確設定。`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "載入儀表板數據失敗";
      setLoadError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setLineUrl(settings.site_line_url || "https://line.me");
  }, [settings]);

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer"
          >
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError("");
    setPwdSuccess("");
    if (!oldPwd) { setPwdError("請輸入舊密碼"); return; }
    if (newPwd.length < 6) { setPwdError("新密碼至少6位"); return; }
    if (newPwd !== confirmPwd) { setPwdError("兩次密碼不一致"); return; }
    try {
      const { error } = await supabase.auth.updateUser({ password: newPwd });
      if (error) throw error;
      setPwdSuccess("密碼修改成功");
      setOldPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err: unknown) {
      setPwdError(err instanceof Error ? err.message : "密碼修改失敗");
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsMsg("");
    if (!lineUrl.trim()) { setSettingsMsg("請輸入有效的 LINE 連結"); return; }
    try {
      const { updateSystemSettings } = await import("@/services/supabaseService");
      await updateSystemSettings({ site_line_url: lineUrl.trim() });
      setSettingsMsg("LINE 團長連結已更新！");
    } catch {
      setSettingsMsg("更新失敗，請稍後再試");
    }
  };

  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter((o) => o.status === "待發貨").length,
    totalMembers: users.filter((u) => u.role === "user").length,
    totalProducts: products.length,
    pendingRecharge: rechargeRequests.filter((r) => r.status === "待審核").length,
    pendingWithdrawals: withdrawals.filter((w) => w.status === "待審核").length,
    pendingKyc: kycSubmissions.filter((k) => k.status === "待審核").length,
    totalRevenue: orders.reduce((sum, o) => sum + Number(o.total_amount), 0),
    pendingTotal:
      orders.filter((o) => o.status === "待發貨").length +
      rechargeRequests.filter((r) => r.status === "待審核").length +
      withdrawals.filter((w) => w.status === "待審核").length +
      kycSubmissions.filter((k) => k.status === "待審核").length,
  };

  // Build monthly trend data from orders
  const monthMap = new Map<string, number>();
  orders.forEach((o) => {
    const m = o.created_at ? o.created_at.slice(0, 7) : "未知";
    monthMap.set(m, (monthMap.get(m) || 0) + Number(o.total_amount));
  });
  const sortedMonths = Array.from(monthMap.entries()).sort().slice(-6);

  const statCards = [
    {
      label: "進度中訂單",
      value: stats.pendingOrders,
      sub: `總訂單 ${stats.totalOrders}`,
      icon: "ri-file-list-3-line",
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      link: "/admin/orders",
    },
    {
      label: "總累積金額",
      value: `NT$ ${stats.totalRevenue.toLocaleString()}`,
      sub: "全站交易總額",
      icon: "ri-money-cny-circle-line",
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      link: "/admin/orders",
    },
    {
      label: "已註冊會員",
      value: stats.totalMembers,
      sub: `商品總數 ${stats.totalProducts}`,
      icon: "ri-team-line",
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
      link: "/admin/members",
    },
    {
      label: "待審核異常",
      value: stats.pendingTotal,
      sub: "儲值/提現/實名",
      icon: "ri-error-warning-line",
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
      link: "/admin/recharge",
    },
  ];

  const quickActions = [
    { label: "新增金額", icon: "ri-add-circle-line", color: "bg-blue-600 hover:bg-blue-700", link: "/admin/recharge" },
    { label: "新增會員", icon: "ri-user-add-line", color: "bg-emerald-600 hover:bg-emerald-700", link: "/admin/members" },
    { label: "匯出報表", icon: "ri-download-cloud-2-line", color: "bg-slate-700 hover:bg-slate-800", onClick: () => alert("報表匯出功能即將推出") },
    { label: "系統設定", icon: "ri-settings-3-line", color: "bg-amber-500 hover:bg-amber-600", onClick: () => setSettingsOpen(!settingsOpen) },
  ];

  const navCards = [
    { label: "儲值審核", desc: "審核用戶儲值申請與手動入帳", href: "/admin/recharge", icon: "ri-wallet-3-line", color: "bg-amber-50 text-amber-600", count: stats.pendingRecharge },
    { label: "商品管理", desc: "新增、編輯、上下架商品與定價", href: "/admin/products", icon: "ri-shopping-bag-3-line", color: "bg-blue-50 text-blue-600", count: stats.totalProducts },
    { label: "訂單管理", desc: "查看訂單、填寫物流、完成訂單", href: "/admin/orders", icon: "ri-truck-line", color: "bg-emerald-50 text-emerald-600", count: stats.pendingOrders },
    { label: "會員管理", desc: "查看分銷商資料與調整餘額", href: "/admin/members", icon: "ri-team-line", color: "bg-purple-50 text-purple-600", count: stats.totalMembers },
    { label: "提現審核", desc: "審核分銷商提現申請與退款管理", href: "/admin/withdrawals", icon: "ri-bank-card-line", color: "bg-red-50 text-red-600", count: stats.pendingWithdrawals },
    { label: "實名審核", desc: "審核分銷商實名認證申請", href: "/admin/kyc", icon: "ri-id-card-line", color: "bg-stone-50 text-stone-600", count: stats.pendingKyc },
  ];

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Welcome bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">商家專區</h1>
            <p className="text-sm text-gray-500 mt-0.5">歡迎回來，{user.name}！這裡是您的管理總覽</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setPwdOpen(!pwdOpen); setSettingsOpen(false); }}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-600 hover:text-amber-600 hover:border-amber-200 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap shadow-sm"
            >
              <i className="ri-lock-password-line text-xs"></i>
              修改密碼
            </button>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-600 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap shadow-sm"
            >
              <i className="ri-logout-box-r-line text-xs"></i>
              登出
            </button>
          </div>
        </div>

        {/* Password Change Panel */}
        {pwdOpen && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-lock-password-line text-amber-600 text-sm"></i>
              修改管理員密碼
            </h3>
            <form onSubmit={handleChangePassword} className="max-w-md space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">舊密碼</label>
                <input type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} placeholder="輸入目前密碼" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">新密碼</label>
                <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="至少6位" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">確認新密碼</label>
                <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} placeholder="再次輸入新密碼" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400" />
              </div>
              {pwdError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-600">{pwdError}</div>}
              {pwdSuccess && <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-600">{pwdSuccess}</div>}
              <div className="flex gap-2 pt-1">
                <button type="submit" className="px-5 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer">確認修改</button>
                <button type="button" onClick={() => { setPwdOpen(false); setPwdError(""); setPwdSuccess(""); }} className="px-5 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 cursor-pointer">取消</button>
              </div>
            </form>
          </div>
        )}

        {/* Settings Panel */}
        {settingsOpen && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-settings-3-line text-amber-600 text-sm"></i>
              系統設置
            </h3>
            <form onSubmit={handleSaveSettings} className="max-w-md space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">LINE 團長跳轉連結</label>
                <input type="url" value={lineUrl} onChange={(e) => setLineUrl(e.target.value)} placeholder="https://line.me/..." className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400" />
                <p className="text-xs text-gray-400 mt-1">儲值頁面的「聯繫團長」按鈕將跳轉至此連結</p>
              </div>
              {settingsMsg && (
                <div className={`rounded-lg px-3 py-2 text-sm ${settingsMsg.includes("更新") ? "bg-emerald-50 border border-emerald-200 text-emerald-600" : "bg-red-50 border border-red-200 text-red-600"}`}>{settingsMsg}</div>
              )}
              <div className="flex gap-2 pt-1">
                <button type="submit" className="px-5 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer">保存設定</button>
                <button type="button" onClick={() => { setSettingsOpen(false); setSettingsMsg(""); }} className="px-5 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 cursor-pointer">取消</button>
              </div>
            </form>
          </div>
        )}

        {loadError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3 shadow-sm">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-500 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-red-600">資料載入異常</p>
              <p className="text-xs text-red-500 mt-0.5">{loadError}</p>
              <button onClick={loadData} className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium cursor-pointer">
                點擊重新載入
              </button>
            </div>
          </div>
        )}

        {/* Top 4 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <Link
              key={card.label}
              to={card.link}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${card.iconBg} ${card.iconColor} flex items-center justify-center`}>
                  <i className={`${card.icon} text-lg`}></i>
                </div>
                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-1">{card.label}</p>
              <p className="text-xl font-bold text-gray-900">{card.value}</p>
              <p className="text-[11px] text-gray-400 mt-1">{card.sub}</p>
            </Link>
          ))}
        </div>

        {/* Middle: Monthly Trend Table + Side navigation cards */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Trend Table */}
          <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-bar-chart-line text-amber-600 text-sm"></i>
                月度總資本額趨勢
              </h3>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">月份</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">總帳金額</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">趨勢變化</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sortedMonths.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-5 py-8 text-center text-gray-400 text-sm">
                        暫無訂單數據
                      </td>
                    </tr>
                  ) : (
                    sortedMonths.map(([month, amount], idx) => {
                      const prev = idx > 0 ? sortedMonths[idx - 1][1] : amount;
                      const change = prev === 0 ? 0 : Math.round(((amount - prev) / prev) * 100);
                      const isUp = change >= 0;
                      return (
                        <tr key={month} className="hover:bg-gray-50/50">
                          <td className="px-5 py-3.5 text-gray-800 font-medium">{month}</td>
                          <td className="px-5 py-3.5 font-bold text-gray-900">
                            NT$ {amount.toLocaleString()}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`text-xs font-medium flex items-center gap-1 ${isUp ? "text-emerald-600" : "text-red-500"}`}>
                              <i className={`${isUp ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
                              {isUp ? "+" : ""}{change}%
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right side nav cards */}
          <div className="space-y-3">
            {navCards.map((card) => (
              <Link
                key={card.label}
                to={card.href}
                className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${card.icon} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">{card.label}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">{card.desc}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-sm font-bold ${card.count > 0 && card.label.includes("審核") ? "text-red-600" : card.count > 0 && card.label.includes("訂單") ? "text-amber-600" : "text-gray-700"}`}>
                    {card.count}
                  </span>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-amber-500 transition-colors"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-flashlight-line text-amber-600 text-sm"></i>
            快速操作
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action) =>
              action.link ? (
                <Link
                  key={action.label}
                  to={action.link}
                  className={`flex flex-col items-center gap-2 py-4 rounded-xl text-white text-sm font-medium transition-colors shadow-sm cursor-pointer ${action.color}`}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20">
                    <i className={`${action.icon} text-base`}></i>
                  </div>
                  <span className="whitespace-nowrap">{action.label}</span>
                </Link>
              ) : (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  className={`flex flex-col items-center gap-2 py-4 rounded-xl text-white text-sm font-medium transition-colors shadow-sm cursor-pointer ${action.color}`}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20">
                    <i className={`${action.icon} text-base`}></i>
                  </div>
                  <span className="whitespace-nowrap">{action.label}</span>
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}